import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrophy } from "@fortawesome/free-solid-svg-icons";
import { useLeagueMembers } from "../../hooks/league/useFetchLeagueMembers";

const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  useEffect(() => {
    if (!targetDate) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };

    setTimeLeft(calculateTimeLeft());
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate]);

  return timeLeft;
};

const LeagueHeader = ({
  activeLeagueCount = 0,
  selectedLeagueId = null,
  userId,
  nextEventDate = null,
}) => {
  const timeLeft = useCountdown(nextEventDate);
  const hasTimeLeft =
    nextEventDate &&
    (timeLeft.days > 0 ||
      timeLeft.hours > 0 ||
      timeLeft.minutes > 0 ||
      timeLeft.seconds > 0);

  // Fetch members for the selected (expanded) league
  const { data: selectedLeagueMembersData } = useLeagueMembers(
    selectedLeagueId,
    1,
    100,
    "",
  );

  // Calculate user's place and points away from 1st
  const { userPlace, pointsAway } = useMemo(() => {
    if (!selectedLeagueId || !userId) {
      return { userPlace: null, pointsAway: null };
    }

    const members = selectedLeagueMembersData?.data;
    if (!members || members.length === 0) {
      return { userPlace: null, pointsAway: null };
    }

    const userIndex = members.findIndex((m) => m.userId === userId);
    if (userIndex === -1) {
      return { userPlace: null, pointsAway: null };
    }

    const place = userIndex + 1;
    const firstPlacePoints = members[0]?.points || 0;
    const userPoints = members[userIndex]?.points || 0;
    const away = place === 1 ? 0 : (firstPlacePoints - userPoints).toFixed(1);

    return { userPlace: place, pointsAway: away };
  }, [selectedLeagueId, selectedLeagueMembersData, userId]);

  return (
    <div className="bg-gradient-to-br from-dark-grey via-gray-700 to-gray-800 rounded-xl p-4 text-white shadow-lg mb-4">
      {/* Top row: Title + Countdown */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-deep-red flex items-center justify-center shadow-lg">
            <FontAwesomeIcon icon={faTrophy} className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">League Hub</h1>
            <p className="text-white/70 text-xs">My Leagues</p>
          </div>
        </div>

        {/* Event Countdown */}
        {hasTimeLeft && (
          <div className="flex flex-col items-end">
            <span className="text-[10px] font-medium text-white/60 uppercase tracking-wide mb-1">
              Event starts
            </span>
            <div className="flex items-center gap-1">
              {[
                { value: timeLeft.days, label: "D" },
                { value: timeLeft.hours, label: "H" },
                { value: timeLeft.minutes, label: "M" },
                { value: timeLeft.seconds, label: "S" },
              ].map((item, index) => (
                <div key={item.label} className="flex items-center gap-1">
                  <div className="flex flex-col items-center">
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-white/15 text-white">
                      <span className="text-xs font-bold tabular-nums">
                        {String(item.value).padStart(2, "0")}
                      </span>
                    </div>
                    <span className="mt-0.5 text-[8px] text-white/50">
                      {item.label}
                    </span>
                  </div>
                  {index < 3 && (
                    <span className="mb-3 text-xs font-light text-white/30">
                      :
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-white">
            {activeLeagueCount}
          </div>
          <div className="text-white/60 text-[10px] uppercase tracking-wide">
            Active
          </div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-gold">
            {userPlace !== null ? `#${userPlace}` : "—"}
          </div>
          <div className="text-white/60 text-[10px] uppercase tracking-wide">
            Place
          </div>
        </div>
        <div className="bg-white/10 rounded-lg p-3 text-center">
          <div className="text-lg font-bold text-white">
            {pointsAway !== null ? (pointsAway === 0 ? "🥇" : pointsAway) : "—"}
          </div>
          <div className="text-white/60 text-[10px] uppercase tracking-wide">
            Pts Away
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeagueHeader;
